import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload,
  LogOut,
  AlertCircle,
  Loader2,
  CheckCircle2,
  FolderUp,
  FileText,
  MessageSquare,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { googleDriveExportService } from '@/services/googleDriveExportService';
import type { ExportProgress, ExportResult } from '@/services/googleDriveExportService';
import {
  GOOGLE_CONFIG,
  GOOGLE_DRIVE_SCOPES,
  fetchUserProfile,
  getStoredProfile,
  getStoredToken,
  saveToken,
  revokeAndClear,
} from '@/lib/googleAuth';

interface GoogleDriveExportProps {
  documents?: Array<{ id: string; title: string; content: string }>;
  chatThreads?: Array<{
    id: string;
    title: string;
    messages: Array<{ role: string; content: string; timestamp?: string }>;
  }>;
  onExportComplete?: (results: { successful: ExportResult[]; failed: string[] }) => void;
}

export const GoogleDriveExport: React.FC<GoogleDriveExportProps> = ({
  documents = [],
  chatThreads = [],
  onExportComplete,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  const [exportType, setExportType] = useState<'documents' | 'chats' | 'both'>('documents');
  const [folderName, setFolderName] = useState('DEEPLY SMART Backup');
  const [format, setFormat] = useState<'md' | 'txt' | 'html'>('md');
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const [exportResults, setExportResults] = useState<{ successful: ExportResult[]; failed: string[] } | null>(null);

  const config = GOOGLE_CONFIG;

  useEffect(() => {
    const loadScripts = async () => {
      if (document.querySelector('script[src*="gapi"]')) {
        setScriptsLoaded(true);
        return;
      }

      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      document.body.appendChild(gapiScript);

      const gsiScript = document.createElement('script');
      gsiScript.src = 'https://accounts.google.com/gsi/client';
      gsiScript.async = true;
      gsiScript.defer = true;
      document.body.appendChild(gsiScript);

      await Promise.all([
        new Promise((resolve) => { gapiScript.onload = resolve; }),
        new Promise((resolve) => { gsiScript.onload = resolve; }),
      ]);

      setScriptsLoaded(true);
    };

    loadScripts();
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    const profile = getStoredProfile();
    if (token) {
      setAccessToken(token);
      googleDriveExportService.setAccessToken(token);
      if (profile) {
        setUserProfile(profile);
      }
    }
  }, []);

  useEffect(() => {
    setSelectedDocIds(new Set(documents.map(d => d.id)));
  }, [documents]);

  useEffect(() => {
    setSelectedThreadIds(new Set(chatThreads.map(t => t.id)));
  }, [chatThreads]);

  const handleAuth = useCallback(async () => {
    if (!scriptsLoaded) {
      setError('Google scripts not loaded yet. Please wait...');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
        client_id: config.clientId,
        scope: GOOGLE_DRIVE_SCOPES,
        callback: async (response: any) => {
          if (response.error) {
            setError(`Authentication failed: ${response.error}`);
            setIsLoading(false);
            return;
          }

          setAccessToken(response.access_token);
          googleDriveExportService.setAccessToken(response.access_token);

          try {
            const userInfo = await fetchUserProfile(response.access_token);
            setUserProfile(userInfo);
            saveToken(response.access_token, userInfo);
          } catch {
            saveToken(response.access_token, null);
          }

          setIsLoading(false);
        },
      });

      tokenClient.requestAccessToken();
    } catch (err) {
      console.error(err);
      setError('Failed to initialize auth client.');
      setIsLoading(false);
    }
  }, [scriptsLoaded, config.clientId]);

  const handleDisconnect = useCallback(async () => {
    await revokeAndClear(accessToken);
    setAccessToken(null);
    setUserProfile(null);
  }, [accessToken]);

  const toggleDocSelection = (id: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleThreadSelection = (id: string) => {
    setSelectedThreadIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleExport = async () => {
    if (!accessToken) return;

    setIsExporting(true);
    setExportProgress(null);
    setExportResults(null);

    const allSuccessful: ExportResult[] = [];
    const allFailed: string[] = [];

    try {
      if (exportType === 'documents' || exportType === 'both') {
        const selectedDocs = documents.filter(d => selectedDocIds.has(d.id));
        if (selectedDocs.length > 0) {
          const docFolderName = exportType === 'both' ? `${folderName}/Documents` : folderName;
          const result = await googleDriveExportService.batchExportDocuments(
            selectedDocs,
            docFolderName,
            format,
            setExportProgress
          );
          allSuccessful.push(...result.successful);
          allFailed.push(...result.failed);
        }
      }

      if (exportType === 'chats' || exportType === 'both') {
        const selectedThreads = chatThreads.filter(t => selectedThreadIds.has(t.id));
        if (selectedThreads.length > 0) {
          const chatFolderName = exportType === 'both' ? `${folderName}/Chat Threads` : folderName;
          const result = await googleDriveExportService.batchExportChatThreads(
            selectedThreads,
            chatFolderName,
            format,
            setExportProgress
          );
          allSuccessful.push(...result.successful);
          allFailed.push(...result.failed);
        }
      }

      setExportResults({ successful: allSuccessful, failed: allFailed });
      onExportComplete?.({ successful: allSuccessful, failed: allFailed });
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const progressPercentage = exportProgress
    ? Math.round((exportProgress.completed / exportProgress.total) * 100)
    : 0;

  if (!accessToken) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderUp className="h-5 w-5" />
            Export to Google Drive
          </CardTitle>
          <CardDescription>
            Back up your documents and chat threads to Google Drive
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          <Button onClick={handleAuth} disabled={isLoading || !scriptsLoaded}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Connect Google Drive
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderUp className="h-5 w-5" />
              Export to Google Drive
            </CardTitle>
            <CardDescription>
              Connected as {userProfile?.email || 'Google User'}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDisconnect}>
            <LogOut className="h-4 w-4 mr-2" />
            Disconnect
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <div className="space-y-3">
          <Label>Folder Name</Label>
          <Input
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="DEEPLY SMART Backup"
          />
        </div>

        <div className="space-y-3">
          <Label>Export Format</Label>
          <RadioGroup value={format} onValueChange={(v) => setFormat(v as 'md' | 'txt' | 'html')}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="md" id="format-md" />
              <Label htmlFor="format-md">Markdown (.md)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="txt" id="format-txt" />
              <Label htmlFor="format-txt">Plain Text (.txt)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="html" id="format-html" />
              <Label htmlFor="format-html">HTML (.html)</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label>What to Export</Label>
          <RadioGroup value={exportType} onValueChange={(v) => setExportType(v as 'documents' | 'chats' | 'both')}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="documents" id="type-docs" />
              <Label htmlFor="type-docs" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documents Only ({documents.length} available)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="chats" id="type-chats" />
              <Label htmlFor="type-chats" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Chat Threads Only ({chatThreads.length} available)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="both" id="type-both" />
              <Label htmlFor="type-both">Both Documents and Chats</Label>
            </div>
          </RadioGroup>
        </div>

        {(exportType === 'documents' || exportType === 'both') && documents.length > 0 && (
          <div className="space-y-2">
            <Label>Select Documents ({selectedDocIds.size}/{documents.length})</Label>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedDocIds.has(doc.id)}
                    onCheckedChange={() => toggleDocSelection(doc.id)}
                  />
                  <span className="text-sm truncate">{doc.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(exportType === 'chats' || exportType === 'both') && chatThreads.length > 0 && (
          <div className="space-y-2">
            <Label>Select Chat Threads ({selectedThreadIds.size}/{chatThreads.length})</Label>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
              {chatThreads.map(thread => (
                <div key={thread.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedThreadIds.has(thread.id)}
                    onCheckedChange={() => toggleThreadSelection(thread.id)}
                  />
                  <span className="text-sm truncate">{thread.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isExporting && exportProgress && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Exporting: {exportProgress.current}</span>
              <span>{exportProgress.completed}/{exportProgress.total} ({progressPercentage}%)</span>
            </div>
            <Progress value={progressPercentage} />
            {exportProgress.failed.length > 0 && (
              <p className="text-sm text-red-600">
                Failed: {exportProgress.failed.length} file(s)
              </p>
            )}
          </div>
        )}

        {exportResults && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700 mb-2">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Export Complete!</span>
            </div>
            <p className="text-sm text-green-600">
              Successfully exported {exportResults.successful.length} file(s) to Google Drive
            </p>
            {exportResults.failed.length > 0 && (
              <p className="text-sm text-red-600 mt-1">
                Failed to export {exportResults.failed.length} file(s): {exportResults.failed.join(', ')}
              </p>
            )}
          </div>
        )}

        <Button
          onClick={handleExport}
          disabled={isExporting || (
            (exportType === 'documents' && selectedDocIds.size === 0) ||
            (exportType === 'chats' && selectedThreadIds.size === 0) ||
            (exportType === 'both' && selectedDocIds.size === 0 && selectedThreadIds.size === 0)
          )}
          className="w-full"
        >
          {isExporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Export to Google Drive
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
